import { useEffect, useMemo, useRef, useState } from "react"
import { AnimatePresence } from "motion/react"
import type { DamageZone } from "../types"
import { AreaPicker } from "./area-picker"
import { CaptureBrief } from "./capture-brief"
import { NativeCapture } from "./native-capture"
import { ContactSheet } from "./contact-sheet"
import { GalleryImport } from "./import/gallery-import"
import { CameraScreen } from "./camera/camera-screen"
import { decideCapability, readEnvironment, type CaptureMode } from "./camera/capability"
import {
  cameraDebugEnabled,
  captureModeOverride,
  resolveStreamSource,
  visionInline,
  type StreamSource,
} from "./camera/stream-source"
import { primeSpeech } from "./camera/voice-coach"
import { primeMotion } from "./motion/use-camera-yaw"
import { loadVision, releaseVision, type VisionClient } from "./brain/vision-client"
import type { BodyType } from "./outlines"
import type { VinActions } from "./vin/use-vin-step"
import type { ShotUploads } from "./uploads/use-shot-uploads"
import type { CaptureState, PlannedShot, ShotId } from "./types"

// The photos phase: "Where's the damage?" → the brief → the walk → Review.
// Owns only which screen is showing and which camera runs the walk; photos
// live in the flow state and the upload hook. Every screen has a sensible
// Back (handed to the flow frame).
//
// Camera: decided once per visit (capability.ts) and only ever stepped down —
// smart (live + the vision engine: car detector, tracer, brain) → guided
// (live, no detector: too slow or missing) → native (the phone's camera
// app: in-app browsers, camera blocked, or the person asked for it). The
// vision engine starts in a worker while they pick zones.

export type CaptureView =
  | { kind: "zones"; returnTo: "review" | null }
  | { kind: "brief" }
  | { kind: "walk"; queue: ShotId[]; index: number; returnTo: "brief" | "review" }
  | { kind: "review" }
  | { kind: "import"; files: File[] }

export function initialCaptureView(capture: CaptureState): CaptureView {
  if (!capture.zonesConfirmed) return { kind: "zones", returnTo: null }
  const anything = Object.keys(capture.shots).length + capture.extras.length + capture.skipped.length
  return anything > 0 ? { kind: "review" } : { kind: "brief" }
}

export function CaptureFlow({
  plan,
  capture,
  uploads,
  onZonesChosen,
  onSkip,
  onAccept,
  onSend,
  sending,
  notice,
  onLater,
  laterBusy,
  onExitBack,
  setBack,
  onViewChange,
  vin,
  bodyType,
  onVoiceChange,
  forceNative = false,
}: {
  plan: readonly PlannedShot[]
  capture: CaptureState
  uploads: ShotUploads
  onZonesChosen: (zones: DamageZone[], unsure: boolean) => void
  onSkip: (id: ShotId) => void
  onAccept: (key: string) => void
  onSend: () => void
  sending: boolean
  notice: string | null
  onLater: (() => void) | null
  laterBusy: boolean
  // Back from the first screen of the photo step (leaves the step).
  onExitBack: () => void
  // Hands this step's Back behaviour to the frame.
  setBack: (handler: (() => void) | null) => void
  // Lets the parent label progress and focus headings per screen.
  onViewChange?: (view: CaptureView) => void
  // The VIN step: read it (barcode, photo, typed), look the car up, confirm.
  vin: VinActions
  // The car's body type (from the VIN's lookup; a sedan otherwise).
  bodyType: BodyType
  onVoiceChange: (on: boolean) => void
  // Desktop uploads: the file picker, never the laptop's webcam.
  forceNative?: boolean
}) {
  const [view, setView] = useState<CaptureView>(() => initialCaptureView(capture))
  const [capability] = useState(() => decideCapability(readEnvironment()))
  const [mode, setMode] = useState<CaptureMode>(() =>
    forceNative
      ? "native"
      : (captureModeOverride(new URLSearchParams(window.location.search)) ?? capability.mode),
  )
  const [source, setSource] = useState<StreamSource | null>(null)
  const [vision, setVision] = useState<VisionClient | null>(null)
  // The camera's "it takes the photo for you" intro: once per visit.
  const [introSeen, setIntroSeen] = useState(false)
  const [debug] = useState(() => cameraDebugEnabled(new URLSearchParams(window.location.search)))

  useEffect(() => {
    if (mode === "native") return
    let alive = true
    void resolveStreamSource(new URLSearchParams(window.location.search)).then((s) => {
      if (alive) setSource(s)
    })
    return () => {
      alive = false
    }
  }, [mode])

  // The vision engine starts in the background (in a worker); until it's
  // ready (or if this phone can't run the detector well) the camera runs
  // guided. The models were fetched during the contact screen.
  useEffect(() => {
    if (mode !== "smart") return
    let alive = true
    void loadVision({ inline: visionInline(new URLSearchParams(window.location.search)) }).then((v) => {
      if (!alive) return
      if (v?.caps.detector) setVision(v)
      else setMode("guided")
    })
    return () => {
      alive = false
    }
  }, [mode])

  // The engine stopped answering (crashed or stuck): the camera goes guided
  // (plain instructions, their shutter) for the rest of the visit.
  useEffect(() => {
    if (!vision) return
    return vision.onLost(() => {
      setVision(null)
      setMode((m) => (m === "smart" ? "guided" : m))
    })
  }, [vision])

  // Done with the photos: the models' memory goes back to the phone.
  useEffect(() => () => releaseVision(), [])

  // The brain also checks photos from the phone's own camera and the
  // gallery: it starts while they read the brief, so the first photo is
  // checked without a wait.
  const warmBrain = view.kind === "brief" || view.kind === "review"
  useEffect(() => {
    if (warmBrain) void loadVision({ inline: visionInline(new URLSearchParams(window.location.search)) })
  }, [warmBrain])

  useEffect(() => {
    onViewChange?.(view)
  }, [view, onViewChange])

  const startWalk = (queue: ShotId[], returnTo: "brief" | "review") => {
    if (queue.length === 0) {
      setView({ kind: "review" })
      return
    }
    // Every way into the camera is a tap: let a voice guide that is already
    // on (kept from before a reload) speak on iOS.
    if (capture.voice) primeSpeech()
    // …and the motion sensors (iPhone asks once), for the walk tracking.
    primeMotion()
    // The car-view brain gets ready while they walk to the first corner, so
    // the first photo gets its check as fast as the rest.
    void loadVision({ inline: visionInline(new URLSearchParams(window.location.search)) })
    setView({ kind: "walk", queue, index: 0, returnTo })
  }

  const openShots = () =>
    plan.filter((s) => !capture.shots[s.id] && !capture.skipped.includes(s.id)).map((s) => s.id)

  // Gallery picker (multi-select, no capture → the photo library).
  const galleryInput = useRef<HTMLInputElement>(null)
  const openGallery = () => galleryInput.current?.click()

  const back = useMemo<(() => void) | null>(() => {
    switch (view.kind) {
      case "import":
        return () => setView({ kind: "review" })
      case "zones":
        return view.returnTo === "review" ? () => setView({ kind: "review" }) : onExitBack
      case "brief":
        return () => setView({ kind: "zones", returnTo: null })
      case "walk":
        return view.index > 0
          ? () => setView({ ...view, index: view.index - 1 })
          : () => setView(view.returnTo === "brief" ? { kind: "brief" } : { kind: "review" })
      case "review":
        return () => setView({ kind: "zones", returnTo: "review" })
    }
  }, [view, onExitBack])

  // A step can leave the plan during the walk: a VIN read from the barcode
  // or typed (no photo) takes the VIN step out. The walk follows, so its
  // count, Back and "first corner" stay right.
  useEffect(() => {
    if (view.kind !== "walk") return
    const current = view.queue[view.index]
    const queue = view.queue.filter((id) => id === current || plan.some((s) => s.id === id))
    if (queue.length !== view.queue.length) setView({ ...view, queue, index: queue.indexOf(current) })
  }, [view, plan])

  useEffect(() => {
    setBack(back)
    return () => setBack(null)
  }, [back, setBack])

  const advance = () => {
    if (view.kind !== "walk") return
    if (view.index + 1 < view.queue.length) setView({ ...view, index: view.index + 1 })
    else setView({ kind: "review" })
  }

  return (
    <>
    <input
      ref={galleryInput}
      type="file"
      accept="image/*"
      multiple
      className="hidden"
      data-testid="gallery-input"
      onChange={(e) => {
        const files = [...(e.target.files ?? [])]
        e.target.value = ""
        if (files.length > 0) setView({ kind: "import", files })
      }}
    />
    <AnimatePresence mode="wait" initial={false}>
      {view.kind === "zones" && (
        <AreaPicker
          key="zones"
          initialZones={capture.zones}
          initialUnsure={capture.unsure}
          onDone={(zones, unsure) => {
            onZonesChosen(zones, unsure)
            if (view.returnTo === "review") {
              setView({ kind: "review" })
            } else {
              setView({ kind: "brief" })
            }
          }}
        />
      )}

      {view.kind === "brief" && (
        <CaptureBrief
          key="brief"
          plan={plan}
          damage={capture.zones}
          onOpen={() => startWalk(openShots(), "brief")}
          onGallery={openGallery}
          onLater={onLater}
          laterBusy={laterBusy}
          liveCamera={mode !== "native"}
          inAppBrowser={capability.inAppBrowser}
        />
      )}

      {view.kind === "walk" && mode !== "native" && source && (
        <CameraScreen
          key="camera"
          plan={plan}
          queue={view.queue}
          index={view.index}
          capture={capture}
          damage={capture.zones}
          source={source}
          vision={mode === "smart" ? vision : null}
          debug={debug}
          uploads={uploads}
          onAccept={onAccept}
          onNext={advance}
          onSkip={(id) => {
            onSkip(id)
            advance()
          }}
          onClose={() => setView(Object.keys(capture.shots).length > 0 ? { kind: "review" } : { kind: "brief" })}
          onUseNative={() => setMode("native")}
          vin={vin}
          bodyType={bodyType}
          voice={capture.voice}
          onVoiceChange={onVoiceChange}
          introSeen={introSeen}
          onIntroSeen={() => setIntroSeen(true)}
        />
      )}

      {view.kind === "walk" && mode === "native" && (
        <NativeCapture
          key="walk"
          plan={plan}
          queue={view.queue}
          index={view.index}
          capture={capture}
          damage={capture.zones}
          uploads={uploads}
          onAccept={onAccept}
          onNext={advance}
          onSkip={(id) => {
            onSkip(id)
            advance()
          }}
          onReview={() => setView({ kind: "review" })}
          vin={vin}
        />
      )}

      {view.kind === "review" && (
        <ContactSheet
          key="review"
          plan={plan}
          capture={capture}
          onTake={(ids) => startWalk(ids, "review")}
          onRetry={uploads.retry}
          onRemove={uploads.remove}
          onAddSpot={() => setView({ kind: "zones", returnTo: "review" })}
          onGallery={openGallery}
          onMove={uploads.move}
          onSend={onSend}
          sending={sending}
          pending={uploads.pending}
          notice={notice}
        />
      )}
      {view.kind === "import" && (
        <GalleryImport
          key="import"
          files={view.files}
          plan={plan}
          capture={capture}
          vision={vision}
          uploads={uploads}
          onTakeMissing={(ids) => startWalk(ids, "review")}
          onReview={() => setView({ kind: "review" })}
        />
      )}
    </AnimatePresence>
    </>
  )
}

